import { useCallback, useState } from 'react';
import Button from '@ingka/button';
import Text from '@ingka/text';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { createValidationResolver } from '@lotta/self-service/lottery-fe/util-validation-resolver';
import type { SelectOccurrenceFormType } from './select-occurrence/SelectOccurrence';
import { SelectOccurrence, SelectOccurrenceFormSchema } from './select-occurrence/SelectOccurrence';
import { occurrenceModules, type OccurrenceModule } from './select-occurrence/occurrences';
import type { SelectWinnerCommunicationFormType } from './select-winner-communication/SelectWinnerCommunication';
import {
  SelectWinnerCommunication,
  SelectWinnerCommunicationFormSchema,
} from './select-winner-communication/SelectWinnerCommunication';
import {
  winnerCommunicationModules,
  type WinnerCommunicationModule,
} from './select-winner-communication/winner-communications';
import styles from './LotteryPreConfig.module.scss';

export type LotteryPreConfigProps = {
  onFormSubmit: (
    winnerCommunication: WinnerCommunicationModule,
    occurrence: OccurrenceModule
  ) => void;
  onSecondaryAction: () => void;
  wrapFormTitle?: (title: string) => React.ReactNode;
  retailUnit: string;
  winnerCommunicationModule?: WinnerCommunicationModule;
  occurrenceModule?: OccurrenceModule;
};

export const LotteryPreConfig: React.FC<LotteryPreConfigProps> = ({
  onFormSubmit,
  onSecondaryAction,
  wrapFormTitle = (title: string) => title,
  retailUnit,
  winnerCommunicationModule: defaultWinnerCommunicationModule,
  occurrenceModule: defaultOccurrenceModule,
}) => {
  const [validation, setValidation] = useState<Record<string, boolean>>({});
  const { t } = useTranslation();

  const isValid = !Object.values(validation).some((isValid) => !isValid);

  const onSubmit = () => {
    const { occurrenceModule } = occurrenceForm.getValues();
    const { winnerCommunicationModule } = winnerCommunicationForm.getValues();
    if (
      occurrenceModules.includes(occurrenceModule) &&
      winnerCommunicationModules.includes(winnerCommunicationModule)
    ) {
      onFormSubmit(winnerCommunicationModule, occurrenceModule);
    }
  };

  const onValidation = useCallback(
    (moduleType: string, isValid: boolean) => {
      setValidation((oldState) => {
        if (oldState?.[moduleType] === isValid) {
          // If no change, return old state to prevent returning a new object
          return oldState;
        }
        return { ...oldState, [moduleType]: isValid };
      });
    },
    [setValidation]
  );

  const occurrenceForm = useForm<SelectOccurrenceFormType>({
    defaultValues: {
      occurrenceModule: defaultOccurrenceModule,
    },
    resolver: createValidationResolver<SelectOccurrenceFormType>({
      ...SelectOccurrenceFormSchema,
    }),
    mode: 'onTouched',
  });

  const winnerCommunicationForm = useForm<SelectWinnerCommunicationFormType>({
    defaultValues: {
      winnerCommunicationModule: defaultWinnerCommunicationModule,
    },
    resolver: createValidationResolver<SelectWinnerCommunicationFormType>({
      ...SelectWinnerCommunicationFormSchema,
    }),
    mode: 'onTouched',
  });

  return (
    <>
      {wrapFormTitle(t('form-contest-configurations'))}
      <div className={styles['container']}>
        <Text headingSize="m" tagName="h2">
          {t('lottery-form-pre-config-title')}
        </Text>
        <Text className={styles['text']}>{t('lottery-form-pre-config-description')}</Text>
        <SelectOccurrence form={occurrenceForm} onValidation={onValidation} />
        <SelectWinnerCommunication
          retailUnit={retailUnit}
          form={winnerCommunicationForm}
          onValidation={onValidation}
        />
      </div>
      <div className={styles['button-container']}>
        <Button
          className={styles['button-confirm']}
          text={`${t('general-next')}`}
          type="primary"
          onClick={onSubmit}
          disabled={!isValid}
          data-testid="button-submit"
        />
        <Button
          className={styles['button-back']}
          text={`${t('general-back')}`}
          onClick={onSecondaryAction}
        />
      </div>
    </>
  );
};
